import { Typography, Box } from '@mui/material';
import {
  getSurveyById,
  surveysSelectors,
  updateAssignmentConfigSuccess,
} from 'redux/slices/surveys.slice';
import { useEffect, useState, useMemo } from 'react';
import { RecordContainer } from '../Styles';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from 'redux/store-hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { FilterOptionDto, SurveyV2 } from '@act/shared/data-transfer-objects';
import { AdminDrawerForm } from '../../AdminDrawer';
import { AssignmentFixedDateConfiguration } from '@act/shared/types';
import { AssignmentRelativeDueConfiguration } from '@act/shared/types';
import { useToast } from 'react-components/app/ReusableToast';
import { FormBuilder } from '@act/storybook';
import { useApiCall } from 'redux/utils/api-hook';
import { surveyAdminRequests } from 'redux/requests/survey-admin.requests';
import useUnsavedChangesDialog from '@act/react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { useAssignmentConfigFormConfig } from './assignment-config-form-config';

const defaultValues: SurveyV2.UpdateAssignmentConfigDto = {
  assignmentConfiguration: {
    clientIds: [],
    segmentIds: [],
    stageIds: [],
    availableWindow: {
      start: null,
      end: null,
    },
  },
  dueAtConfig: {
    offsetValue: null,
    maxMonth: null,
    segmentOverrides: [],
  },
};

export const EditAssignmentConfigDrawer = ({
  isOpen,
  setOpen,
  surveyId,
  clientOptions,
  segmentOptions,
  stageOptions,
}: {
  isOpen: boolean;
  setOpen: (boolean) => void;
  surveyId?: number;
  clientOptions: FilterOptionDto[];
  segmentOptions: FilterOptionDto[];
  stageOptions: FilterOptionDto[];
}) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const survey = surveysSelectors.useSelectSurveyById(surveyId);

  const dueAtConfig =
    survey?.initialCheckpointDueAt &&
    'fixedDate' in survey.initialCheckpointDueAt
      ? (survey.initialCheckpointDueAt as AssignmentFixedDateConfiguration)
      : (survey?.initialCheckpointDueAt as AssignmentRelativeDueConfiguration);

  const initialSegmentOverrides =
    !!dueAtConfig && 'fixedDate' in dueAtConfig
      ? []
      : (dueAtConfig as AssignmentRelativeDueConfiguration)?.segmentOverrides;

  const [segmentOverrides, setSegmentOverridesState] = useState<
    {
      segmentId: number;
      maxMonth?: number;
      offsetValue: 'avgProgramDays' | number;
    }[]
  >(initialSegmentOverrides);

  useEffect(() => {
    if (!survey) {
      dispatch(getSurveyById({ params: { surveyId } }));
    }
  }, []);

  useEffect(() => {
    const dueAtConfig =
      survey?.initialCheckpointDueAt &&
      'fixedDate' in survey.initialCheckpointDueAt
        ? (survey.initialCheckpointDueAt as AssignmentFixedDateConfiguration)
        : (survey?.initialCheckpointDueAt as AssignmentRelativeDueConfiguration);

    const initialSegmentOverrides =
      !!dueAtConfig && 'fixedDate' in dueAtConfig
        ? []
        : (dueAtConfig as AssignmentRelativeDueConfiguration)?.segmentOverrides;

    reset({
      assignmentConfiguration: survey?.assignmentConfiguration,
      dueAtConfig: survey?.initialCheckpointDueAt,
    });
    setSegmentOverridesState(initialSegmentOverrides);
  }, [survey, setOpen]);

  const { control, handleSubmit, reset, formState, setValue } =
    useForm<SurveyV2.UpdateAssignmentConfigRequest>({
      resolver: zodResolver(SurveyV2.UpdateAssignmentConfigSchema),
      defaultValues,
    });

  const setSegmentOverrides = (
    overrides: {
      segmentId: number;
      maxMonth?: number;
      offsetValue: 'avgProgramDays' | number;
    }[]
  ) => {
    setSegmentOverridesState(overrides);
    setValue('dueAtConfig.segmentOverrides', overrides, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const [
    updatedAssignmentConfig,
    _updatedAssignmentConfigLoading,
    updateAssignmentConfig,
  ] = useApiCall(surveyAdminRequests.updateAssignmentConfiguration);

  const formConfig = useAssignmentConfigFormConfig({
    clientOptions,
    segmentOptions,
    stageOptions,
    segmentOverrides,
    setSegmentOverrides,
    control,
    formState,
  });

  const onSubmit = async (data: SurveyV2.UpdateAssignmentConfigRequest) => {
    try {
      await updateAssignmentConfig({
        params: { id: survey.id },
        body: data,
      });
    } catch (error) {
      toast('Failed to update assignment configuration', 'error');
    }
  };
  useEffect(() => {
    if (updatedAssignmentConfig) {
      dispatch(updateAssignmentConfigSuccess(updatedAssignmentConfig));
      toast('Survey updated successfully', 'success');
      setOpen(false);
    }
  }, [updatedAssignmentConfig]);

  const { isDirty } = formState;

  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  const DrawerContent = useMemo(() => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap="24px"
      >
        <RecordContainer>
          <Box
            sx={{
              display: 'flex',
              algnItems: 'center',
              justifyContent: 'space-between',
              alignSelf: 'stretch',
            }}
          >
            <Typography variant="headlineMedium">
              Assignment Criteria
            </Typography>
          </Box>
          {/* Display form-level errors */}
          {formState.errors.root && (
            <Box sx={{ color: 'error.main', mb: 2 }}>
              <Typography variant="bodySmall" color="error">
                {formState.errors.root.message}
              </Typography>
            </Box>
          )}
          <Box display="flex" flexDirection="column" width="100%">
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
            />
          </Box>
        </RecordContainer>
      </Box>
    );
  }, [control, formConfig, formState.errors]);

  return (
    <AdminDrawerForm
      title={`${survey?.name} Assignment`}
      children={DrawerContent}
      onSubmit={handleSubmit(onSubmit)}
      open={isOpen}
      setOpen={handleClose}
    />
  );
};
